import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, Trash, UploadSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { EMPTY_NOTIFY, NotifyBuyerBox, notifyParams } from "@/components/NotifyBuyerBox";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const KEYS = ["company_name", "legal_name", "contact_name", "contact_email", "contact_phone", "company_email", "phone",
  "thankyou_phone", "office_address", "office_hours", "brand_color"];
const INPUT = "w-full border border-black px-3 py-2 text-sm bg-white";
const BTN = "inline-flex items-center gap-1 border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40 transition-colors duration-100";
const pick = (d) => Object.fromEntries(KEYS.map((k) => [k, d?.[k] || ""]));
const err = (e, f) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : f; };

// Where each saved detail actually appears, so nothing is a guess.
const USES = {
  company_name: ["Landing pages", "Thank-you pages", "Consent text", "Buyer emails"],
  legal_name: ["Consent text", "Privacy & terms pages", "Page footer"],
  contact_name: ["Their login name", "Never on pages"],
  contact_email: ["Buyer update emails", "Never on pages"],
  contact_phone: ["Records only", "Never on pages"],
  company_email: ["Thank-you pages"],
  phone: ["Landing pages", "Thank-you pages if no thank-you phone"],
  thankyou_phone: ["Thank-you pages"],
  office_hours: ["Thank-you pages"],
  office_address: ["Thank-you pages", "Legal checks"],
  brand_color: ["Landing pages", "Thank-you pages"],
  logo: ["Landing pages", "Thank-you pages"],
};
const TAG = {
  "Landing pages": "border-sky-700 bg-sky-50 text-sky-900", "Thank-you pages": "border-emerald-700 bg-emerald-50 text-emerald-900",
  "Records only": "border-zinc-400 bg-zinc-100 text-zinc-600", "Never on pages": "border-zinc-400 bg-zinc-100 text-zinc-600",
};

function Uses({ k }) {
  return (
    <span className="mt-1 flex flex-wrap gap-1" data-testid={`buyer-details-uses-${k.replace(/_/g, "-")}`}>
      {(USES[k] || []).map((u) => (
        <span key={u} className={`border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest ${TAG[u] || "border-amber-700 bg-amber-50 text-amber-900"}`}>{u}</span>
      ))}
    </span>
  );
}

function Field({ label, hint, k, children }) {
  return (
    <label className="block">
      <span className="label-mono">{label}</span>
      {k && <Uses k={k} />}
      <div className="mt-1">{children}</div>
      {hint && <span className="mt-1 block text-[10px] text-zinc-500">{hint}</span>}
    </label>
  );
}

function Text({ k, form, set, ...rest }) {
  return <input value={form[k]} onChange={(e) => set(k, e.target.value)} className={INPUT} data-testid={`buyer-details-${k.replace(/_/g, "-")}`} {...rest} />;
}

function LogoPicker({ orgId, data, onSaved }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { onSaved((await fn()).data); toast.success(ok); } catch (e) { toast.error(err(e, "Logo update failed")); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const upload = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo must be 2 MB or smaller");
    const fd = new FormData();
    fd.append("file", file);
    run(() => api.post(`/admin/clients/${orgId}/details/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } }),
      "Logo updated on all their pages");
  };
  return (
    <Field label="Logo" k="logo" hint="PNG, JPG or WEBP up to 2 MB. Saves right away on all their pages.">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-14 w-28 items-center justify-center overflow-hidden border border-black bg-zinc-900">
          {data.logo_url
            ? <img src={`${BACKEND}${data.logo_url}`} alt="Buyer logo" className="max-h-12 max-w-[104px] object-contain" data-testid="buyer-details-logo-preview" />
            : <span className="text-[9px] uppercase tracking-widest text-zinc-400">No logo</span>}
        </div>
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
               onChange={(e) => upload(e.target.files?.[0])} data-testid="buyer-details-logo-input" />
        <button type="button" className={BTN} disabled={busy || !data.pages.length} onClick={() => ref.current?.click()} data-testid="buyer-details-logo-upload">
          <UploadSimple size={11} weight="bold" /> {busy ? "Uploading…" : "Upload"}
        </button>
        {data.logo_url && (
          <button type="button" className={BTN} disabled={busy} data-testid="buyer-details-logo-remove"
                  onClick={() => run(() => api.delete(`/admin/clients/${orgId}/details/logo`), "Logo removed")}>
            <Trash size={11} weight="bold" /> Remove
          </button>
        )}
      </div>
    </Field>
  );
}

// Manage Buyer → "Edit details": company, contact, phones, office, logo and color for every page they own.
export function BuyerDetailsEditor({ orgId, onSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [notify, setNotify] = useState(EMPTY_NOTIFY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/admin/clients/${orgId}/details`).then((r) => { setData(r.data); setForm(pick(r.data)); })
      .catch((e) => toast.error(err(e, "Couldn't load buyer details")));
  }, [orgId]);
  if (!data || !form) return <div className="label-mono">Loading details…</div>;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = KEYS.some((k) => (form[k] || "") !== (data[k] || ""));
  const body = () => ({ ...form, contact_email: form.contact_email.trim() || null, company_email: form.company_email.trim() || null });
  const save = async () => {
    if (!form.company_name.trim()) return toast.error("Company name is required");
    setSaving(true);
    try {
      const { data: d } = await api.put(`/admin/clients/${orgId}/details`, body(), notifyParams(notify));
      setData(d); setForm(pick(d)); setNotify(EMPTY_NOTIFY);
      toast.success(`Saved — ${d.updated_pages} page${d.updated_pages === 1 ? "" : "s"} updated`);
      onSaved?.(d);
    } catch (e) { toast.error(err(e, "Couldn't save details")); } finally { setSaving(false); }
  };
  const pages = data.pages.length;
  return (
    <div className="space-y-4" data-testid="buyer-details-editor">
      <div className="border-2 border-dashed border-zinc-400 bg-zinc-50 px-3 py-2 text-[11px]" data-testid="buyer-details-scope">
        {pages
          ? <>Saving updates <b>{pages} main page{pages === 1 ? "" : "s"}</b> ({data.pages.map((p) => `/lp/${p.slug}`).join(", ")})
              {data.lander_count ? <> and <b>{data.lander_count} other page{data.lander_count === 1 ? "" : "s"}</b></> : null}, their thank-you pages,
              shared pages when this buyer gets the lead, and the client record.</>
          : <>This buyer has no page yet, so only the client record and their login change.</>}
        {data.login && <> The contact person is also their login name ({data.login.email}).</>}
        <div className="mt-1">Pages only show the company&apos;s details. The contact person&apos;s name, email and phone are never shown on landing or thank-you pages.</div>
      </div>
      <div className="label-mono" data-testid="buyer-details-private-heading">Contact person (private)</div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Field label="Contact person" k="contact_name"><Text k="contact_name" form={form} set={set} maxLength={80} /></Field>
        <Field label="Contact email" k="contact_email" hint="Their login and buyer emails."><Text k="contact_email" form={form} set={set} type="email" maxLength={120} /></Field>
        <Field label="Contact phone" k="contact_phone"><Text k="contact_phone" form={form} set={set} maxLength={32} /></Field>
      </div>
      <div className="label-mono" data-testid="buyer-details-public-heading">Company details shown on their pages</div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Company name *" k="company_name" hint="The firm name visitors see."><Text k="company_name" form={form} set={set} maxLength={120} /></Field>
        <Field label="Legal company name" k="legal_name" hint="Leave empty to use the company name."><Text k="legal_name" form={form} set={set} maxLength={200} /></Field>
        <Field label="Company email" k="company_email" hint="A company address like intake@firm.com. Leave empty to show no email."><Text k="company_email" form={form} set={set} type="email" maxLength={120} placeholder="intake@firm.com" /></Field>
        <Field label="Landing page phone" k="phone" hint="The call button and header number on their pages."><Text k="phone" form={form} set={set} maxLength={32} placeholder="1-800-555-0199" /></Field>
        <Field label="Thank-you page phone" k="thankyou_phone" hint="Call and text buttons after a visitor submits. Leave empty to use the landing page phone."><Text k="thankyou_phone" form={form} set={set} maxLength={32} /></Field>
        <Field label="Office hours" k="office_hours"><Text k="office_hours" form={form} set={set} maxLength={120} placeholder="Mon–Fri 9am–6pm" /></Field>
        <Field label="Office address" k="office_address">
          <textarea rows={2} value={form.office_address} onChange={(e) => set("office_address", e.target.value)} maxLength={240}
                    className={INPUT} data-testid="buyer-details-office-address" />
        </Field>
        <Field label="Brand color" k="brand_color" hint="Buttons and accents.">
          <div className="flex items-center gap-2">
            <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(form.brand_color) ? form.brand_color : "#000000"}
                   onChange={(e) => set("brand_color", e.target.value)} className="h-9 w-12 border border-black" data-testid="buyer-details-brand-color-picker" />
            <Text k="brand_color" form={form} set={set} maxLength={7} placeholder="#0f172a" />
          </div>
        </Field>
      </div>
      <LogoPicker orgId={orgId} data={data} onSaved={(d) => setData((cur) => ({ ...cur, logo_url: d.logo_url }))} />
      <div className="flex flex-wrap items-start gap-3">
        <button type="button" onClick={save} disabled={saving || !dirty} data-testid="buyer-details-save"
                className="inline-flex items-center gap-2 border border-black bg-black px-4 py-2 text-xs uppercase tracking-widest text-white disabled:opacity-40">
          <FloppyDisk size={12} weight="bold" /> {saving ? "Saving…" : "Save details"}
        </button>
        {dirty && pages > 0 && (
          <div className="w-full md:w-96">
            <NotifyBuyerBox value={notify} onChange={setNotify} testId="buyer-details-notify"
                            preview={() => ({ kind: "details", org_id: orgId, details: body() })} />
          </div>
        )}
      </div>
    </div>
  );
}
